import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Shield,
  Crown,
  Lock,
  Share2,
  Repeat2,
  TrendingUp,
  Sparkles,
  Brain,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useSimulatorI18n } from "../i18n/simulator";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";
// Calculate Trader DNA scores from decisions + trades
const computeScores = (state) => {
  const decisions = state.decisions || [];
  const trades = state.trades || [];
  const initial = state.initialCapital || 10000;
  const final = state.finalCapital || initial;
  const pnlPct = ((final - initial) / initial) * 100;
  const wins = trades.filter((t) => t.pnlPct > 0).length;
  const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;

  const panicCount = decisions.filter((d) => d.includes("panic")).length;
  const revengeCount = decisions.filter((d) => d.includes("revenge")).length;
  const slUsage = trades.filter((t) => t.reason === "sl" || t.reason === "tp").length;
  const slRatio = trades.length > 0 ? (slUsage / trades.length) * 100 : 60;

  const emotional = Math.max(0, Math.min(100, 90 - panicCount * 25 - revengeCount * 20));
  const risk = Math.max(0, Math.min(100, 40 + slRatio * 0.5 + (pnlPct > 0 ? 10 : -5)));
  const discipline = Math.max(0, Math.min(100, 80 - revengeCount * 12 - panicCount * 10));
  const smiq = Math.max(0, Math.min(100, 40 + winRate * 0.55));
  const resilience = Math.max(0, Math.min(100, 70 + Math.min(20, pnlPct) - panicCount * 15));
  const timing = Math.max(0, Math.min(100, 35 + winRate * 0.6));

  const avg = (emotional + risk + discipline + smiq + resilience + timing) / 6;
  let rank, rankColor;
  if (avg < 21) { rank = "RETAIL SURVIVOR"; rankColor = "#ff3355"; }
  else if (avg < 41) { rank = "STRUCTURE READER"; rankColor = "#f5a623"; }
  else if (avg < 61) { rank = "LIQUIDITY HUNTER"; rankColor = "#00d4ff"; }
  else if (avg < 76) { rank = "SMART MONEY ANALYST"; rankColor = "#9b59ff"; }
  else if (avg < 90) { rank = "INSTITUTIONAL OPERATOR"; rankColor = "#00e676"; }
  else { rank = "MARKET PREDATOR"; rankColor = "#ffd700"; }

  return {
    emotional: Math.round(emotional),
    risk: Math.round(risk),
    discipline: Math.round(discipline),
    smiq: Math.round(smiq),
    resilience: Math.round(resilience),
    timing: Math.round(timing),
    avg: Math.round(avg),
    rank,
    rankColor,
    pnlPct,
    winRate,
  };
};

const barColor = (v) => {
  if (v < 41) return "#ff3355";
  if (v < 61) return "#f5a623";
  if (v < 81) return "#00d4ff";
  return "#00e676";
};

export default function SimulatorResultsPage() {
  const { lang } = useLanguage();
  const tSim = useSimulatorI18n(lang);
  const { isPro } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scores = useMemo(() => computeScores(state), [state]);
  const labels = tSim.dna_metrics;
  const items = [
    { label: labels[0], value: scores.emotional },
    { label: labels[1], value: scores.risk },
    { label: labels[2], value: scores.discipline },
    { label: labels[3], value: scores.smiq },
    { label: labels[4], value: scores.resilience },
    { label: labels[5], value: scores.timing },
  ];
  // For FREE, only show 3 metrics
  const visibleItems = isPro ? items : items.slice(0, 3);

  const drawDown = Math.min(0, scores.pnlPct);
  const finalCap = state.finalCapital || 10000;
  const initial = state.initialCapital || 10000;
  const tradeCount = (state.trades || []).length;

  // -------- Phase 3: auto-save (PRO only) + DNA profile --------
  const [savedSessionId, setSavedSessionId] = useState(null);
  const [dnaText, setDnaText] = useState(null);
  const [dnaLoading, setDnaLoading] = useState(false);
  const [dnaError, setDnaError] = useState(null);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  const handleUnlockPro = async () => {
    if (unlockLoading) return;
    setUnlockLoading(true);
    setUnlockError(null);
    try {
      const API = API_BASE; // runtime-resolved
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: "pro_lifetime", origin_url: window.location.origin }),
      });
      const data = await safeJson(res);
      if (res.ok && data?.url) {
        window.location.href = data.url;
        return;
      }
      setUnlockError(true);
    } catch {
      setUnlockError(true);
    } finally {
      setUnlockLoading(false);
    }
  };

  useEffect(() => {
    if (!isPro) return;
    if (!state.scenarioName) return; // skip if visited without state
    if (savedSessionId) return;
    const save = async () => {
      try {
        const API = API_BASE;  // runtime-resolved, see /lib/api.js
        const token = localStorage.getItem("rebel_jwt");
        const res = await fetch(`${API}/api/simulator/save-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            scenario_id: state.scenarioId || "ftx_collapse",
            scenario_name: state.scenarioName,
            final_capital: finalCap,
            initial_capital: initial,
            pnl_pct: scores.pnlPct,
            max_drawdown: drawDown,
            duration_candles: 80,
            trades: state.trades || [],
            decisions: state.decisions || [],
            scores: {
              emotional: scores.emotional,
              risk: scores.risk,
              discipline: scores.discipline,
              smiq: scores.smiq,
              resilience: scores.resilience,
              timing: scores.timing,
              avg: scores.avg,
              rank: scores.rank,
            },
            rank: scores.rank,
          }),
        });
        const data = await safeJson(res);
        if (res.ok && data?.session?.id) setSavedSessionId(data.session.id);
      } catch {
        /* silent */
      }
    };
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, state.scenarioName]);

  const generateProfile = async () => {
    if (dnaLoading) return;
    setDnaLoading(true);
    setDnaError(null);
    try {
      const API = API_BASE;  // runtime-resolved, see /lib/api.js
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/api/simulator/dna-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          lang,
          scenario_name: state.scenarioName || "RE₿EL Run",
          pnl_pct: scores.pnlPct,
          decisions: state.decisions || [],
          scores: {
            emotional: scores.emotional,
            risk: scores.risk,
            discipline: scores.discipline,
            smiq: scores.smiq,
            resilience: scores.resilience,
            timing: scores.timing,
            avg: scores.avg,
            rank: scores.rank,
          },
        }),
      });
      const data = await safeJson(res);
      if (res.ok && data.analysis) setDnaText(data.analysis);
      else setDnaError(data.detail || "error");
    } catch (e) {
      setDnaError("network");
    } finally {
      setDnaLoading(false);
    }
  };

  const shareText = `Acabo de sobrevivir el escenario ${state.scenarioName || "RE₿EL"} en el RE₿EL MARKET SIMULATOR™
🎯 Rango: ${scores.rank}
🧠 Disciplina: ${scores.discipline}/100
⚡ Smart Money IQ: ${scores.smiq}/100
¿Puedes superarme? www.rebelbtc.com/simulator`;

  const copyShare = () => {
    try {
      navigator.clipboard.writeText(shareText);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#05060d] text-white antialiased" data-testid="sim-results-page">
      <Navbar />
      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(0,212,255,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-6 md:px-12">
          <Link
            to="/simulator"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            RE₿EL Market Simulator™
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mt-6"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-emerald-300/80">
              Simulación completada
            </p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl md:text-6xl" data-testid="sim-results-title">
              {state.scenarioName || "RE₿EL Run"}
            </h1>
          </motion.div>

          {/* Financial summary */}
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Capital final" value={`$${finalCap.toFixed(0)}`} />
            <Stat
              label="PnL total"
              value={`${scores.pnlPct >= 0 ? "+" : ""}${scores.pnlPct.toFixed(2)}%`}
              color={scores.pnlPct >= 0 ? "#00e676" : "#ff3355"}
            />
            <Stat label="Max DD" value={`${drawDown.toFixed(2)}%`} color="#ff3355" />
            <Stat label="Trades" value={String(tradeCount)} />
          </div>

          {/* Rank badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-14 flex flex-col items-center"
            data-testid="sim-rank-badge"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/40">Tu rango</p>
            <div
              className="mt-3 inline-flex items-center gap-3 rounded px-8 py-3 text-base font-black uppercase tracking-[0.22em]"
              style={{
                background:
                  scores.rank === "MARKET PREDATOR"
                    ? "linear-gradient(135deg, #ffd700, #ff8c00)"
                    : `${scores.rankColor}1c`,
                color: scores.rank === "MARKET PREDATOR" ? "#0a0a10" : scores.rankColor,
                border: `1px solid ${scores.rankColor}66`,
                boxShadow: `0 0 40px ${scores.rankColor}55`,
              }}
            >
              <Crown className="h-4 w-4" />
              {scores.rank}
            </div>
            <p className="mt-3 font-mono text-xs text-white/40">
              Score promedio: <span className="text-white/80">{scores.avg}/100</span>
            </p>
          </motion.div>

          {/* DNA bars */}
          <div className="mt-14">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-purple-300/80">
              Trader DNA™
            </p>
            <h2 className="mt-3 font-display text-2xl font-black tracking-tight text-white md:text-3xl">
              {tSim.dna_title}
            </h2>
            <div className="mt-6 space-y-4 rounded-3xl border border-white/10 bg-black/40 p-6 backdrop-blur-xl md:p-8">
              {visibleItems.map((it, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.08 }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75">
                      {it.label}
                    </span>
                    <span className="font-mono text-xs text-white/45">{it.value}/100</span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${it.value}%` }}
                      transition={{ duration: 1.2, delay: 0.7 + i * 0.08, ease: [0.33, 1, 0.68, 1] }}
                      className="h-full rounded-full"
                      style={{ background: barColor(it.value), boxShadow: `0 0 10px ${barColor(it.value)}66` }}
                    />
                  </div>
                </motion.div>
              ))}
              {!isPro && (
                <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                  <p className="text-xs text-amber-100">
                    Las 3 métricas restantes (Smart Money IQ, Resiliencia, Timing) se desbloquean con RE₿EL PRO.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* AI psychological profile */}
          <div className="mt-10 rounded-3xl border border-purple-400/25 bg-gradient-to-br from-purple-500/[0.06] via-white/[0.02] to-transparent p-6 backdrop-blur-xl md:p-8">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-purple-400/30 bg-purple-400/10">
                <Brain className="h-4 w-4 text-purple-300" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-purple-300/80">
                  Perfil psicológico IA
                </p>
                <h3 className="mt-2 font-display text-2xl font-black tracking-tight md:text-3xl">
                  Análisis personalizado generado por IA
                </h3>
                <p className="mt-3 text-sm text-white/55">
                  Claude Sonnet 4.5 analiza tus scores, decisiones y patrón dominante. Identifica sesgos,
                  fortaleza principal y una recomendación accionable.
                </p>

                {dnaText ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-5 font-mono text-[13px] leading-relaxed text-white/85"
                    data-testid="sim-dna-text"
                  >
                    {dnaText}
                  </motion.div>
                ) : (
                  <button
                    type="button"
                    onClick={generateProfile}
                    disabled={dnaLoading}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-400 to-fuchsia-400 px-6 py-3 text-xs font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(155,89,255,0.4)] disabled:cursor-wait disabled:opacity-60"
                    data-testid="sim-dna-generate-btn"
                  >
                    <Sparkles className="h-4 w-4" />
                    {dnaLoading ? "Generando…" : "Generar mi perfil completo"}
                  </button>
                )}

                {dnaError && (
                  <p className="mt-3 text-xs text-rose-300">
                    No pudimos generar el perfil ahora. Intenta de nuevo en unos segundos.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {isPro ? (
              <button
                onClick={() => navigate("/simulator/play")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-6 py-3 text-xs font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(0,230,118,0.4)]"
                data-testid="sim-results-next"
              >
                <Repeat2 className="h-4 w-4" />
                Siguiente simulación
              </button>
            ) : (
              <button
                onClick={() => navigate("/simulator")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white/80 transition-all hover:bg-white/10"
                data-testid="sim-results-back-menu"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver al menú
              </button>
            )}
            {isPro && (
              <button
                onClick={() => navigate("/simulator/history")}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white/80 transition-all hover:bg-white/10"
                data-testid="sim-results-history"
              >
                <TrendingUp className="h-4 w-4" />
                Ver mi historial
              </button>
            )}
            {!isPro && (
              <div className="flex flex-col gap-1">
                <button
                  onClick={handleUnlockPro}
                  disabled={unlockLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(255,140,0,0.55)] disabled:cursor-wait disabled:opacity-60"
                  data-testid="sim-results-unlock"
                >
                  <Crown className="h-4 w-4" />
                  {unlockLoading ? "Conectando…" : "Desbloquear PRO · €39.90"}
                </button>
                {unlockError && (
                  <p className="text-[10px] text-rose-300" data-testid="sim-results-unlock-error">
                    No pudimos abrir el checkout. Reintenta en unos segundos.
                  </p>
                )}
              </div>
            )}
            {isPro && (
              <button
                onClick={copyShare}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-purple-400/40 bg-purple-400/10 px-6 py-3 text-xs font-bold uppercase tracking-wider text-purple-200 transition-all hover:bg-purple-400/20"
                data-testid="sim-results-share"
              >
                <Share2 className="h-4 w-4" />
                Compartir mi resultado
              </button>
            )}
          </div>

          <div className="mt-12 flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-4">
            <Shield className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
            <p className="text-xs text-white/60">
              RE₿EL BTC ofrece exclusivamente contenido educativo. No es asesoramiento financiero.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Stat = ({ label, value, color = "#ffffff" }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/45">{label}</p>
    <p className="mt-2 font-display text-2xl font-black tracking-tighter" style={{ color }}>
      {value}
    </p>
  </div>
);
